import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

// Scroll Restoration component
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

export default function Layout({ children }) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();

  const menuItems = [
    { label: 'Forside', path: '/' },
    { label: 'Vores enheder', path: '/feriehuse' },
    { label: 'Lej Campen', path: '/lej-campen' },
    { label: 'Om Campen Camping', path: '/om-campen' },
    { label: 'Kontakt', path: '/kontakt' },
  ];

  const isActive = (path) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#fbfbf9] text-stone-800 font-sans antialiased">
      <ScrollToTop />

      {/* Header */}
      <header className="sticky top-0 z-40 bg-white/95 backdrop-blur-md text-stone-800 shadow-sm transition-all duration-300 border-b border-stone-200/80">
        <div className="mx-auto max-w-7xl px-6 py-4 flex flex-col items-center gap-4">
          {/* Logo Row - Centered on desktop, left-aligned on mobile */}
          <div className="w-full flex items-center justify-start min-[1200px]:justify-center relative">
            <Link to="/" className="flex items-center gap-3 group">
              <img
                src="https://assets.auxodev.dk/assets/d026a2b2-f90d-4c19-95d2-042cbc8d93d8/campen-camping-logo-transparent-512x67v2.png"
                alt="Campen Camping Logo"
                className="h-10 md:h-12 w-auto max-w-[250px] md:max-w-none object-contain transition-transform duration-300 group-hover:scale-[1.02]"
              />
            </Link>

            {/* Mobile Menu Button (Visible on screens < 1200px, absolutely positioned on the right) */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="min-[1200px]:hidden text-stone-800 hover:text-[#67aedd] text-2xl focus:outline-none transition-colors p-2 absolute right-0 top-1/2 -translate-y-1/2"
              aria-label="Toggle menu"
            >
              <i className={`fa-solid ${isMenuOpen ? 'fa-xmark' : 'fa-bars'}`} />
            </button>
          </div>

          {/* Desktop Navigation (Visible on screens >= 1200px, centered below logo) */}
          <nav className="hidden min-[1200px]:flex items-center justify-center gap-8 w-full border-t border-stone-200/80 pt-3 mt-1">
            {menuItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`relative py-2 text-sm font-medium tracking-wide transition-colors duration-300 hover:text-[#67aedd] ${
                  isActive(item.path) ? 'text-[#67aedd] font-semibold' : 'text-stone-600'
                }`}
              >
                {item.label}
                {isActive(item.path) && (
                  <span className="absolute bottom-0 left-0 w-full h-0.5 bg-[#67aedd] rounded-full animate-fade-in" />
                )}
              </Link>
            ))}
            <Link
              to="/feriehuse"
              className="bg-[#67aedd] hover:bg-[#5aa1cc] text-white font-bold px-6 py-2.5 rounded-full shadow-sm hover:shadow-md transition-all duration-300 text-sm"
            >
              <i className="fa-solid fa-calendar-days mr-2" /> Book ophold
            </Link>
          </nav>
        </div>

        {/* Mobile Navigation Drawer (Visible on screens < 1200px) */}
        {isMenuOpen && (
          <div className="min-[1200px]:hidden bg-white border-t border-stone-200 shadow-lg animate-slide-down">
            <div className="mx-auto max-w-7xl px-6 py-6 flex flex-col gap-4">
              {menuItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setIsMenuOpen(false)}
                  className={`py-2.5 text-base font-medium border-b border-stone-100 transition-colors ${
                    isActive(item.path) ? 'text-[#67aedd] font-bold pl-2 border-l-2 border-l-[#67aedd]' : 'text-stone-600'
                  }`}
                >
                  {item.label}
                </Link>
              ))}
              <Link
                to="/feriehuse"
                onClick={() => setIsMenuOpen(false)}
                className="mt-2 w-full text-center bg-[#67aedd] hover:bg-[#5aa1cc] text-white font-bold py-3 rounded-xl shadow-sm hover:shadow-md transition-all duration-300"
              >
                <i className="fa-solid fa-calendar-days mr-2" /> Book ophold
              </Link>
            </div>
          </div>
        )}
      </header>

      {/* Main Content */}
      <main className="flex-grow">
        {children}
      </main>

      {/* Footer */}
      <footer className="bg-stone-50 text-stone-700 border-t border-stone-200/80 pt-16 pb-8">
        <div className="mx-auto max-w-7xl px-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          {/* Brand Col */}
          <div className="flex flex-col gap-4">
            <img
              src="https://assets.auxodev.dk/assets/d026a2b2-f90d-4c19-95d2-042cbc8d93d8/campen-camping-logo-transparent-512x67v2.png"
              alt="Campen Camping"
              className="h-10 w-auto object-contain self-start"
            />
            <p className="text-stone-600 text-sm leading-relaxed mt-2">
              Oplev den fredelige og ugenerte camping- og feriestemning på Samsøs sydligste campingplads med overdådig havudsigt mod vest.
            </p>
          </div>

          {/* Quick Links Col */}
          <div>
            <h4 className="text-stone-900 font-serif font-semibold text-lg mb-4">Hurtige links</h4>
            <ul className="flex flex-col gap-2.5 text-sm text-stone-600">
              {menuItems.map((item) => (
                <li key={item.path}>
                  <Link to={item.path} className="hover:text-[#67aedd] transition-colors">
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Col */}
          <div>
            <h4 className="text-stone-900 font-serif font-semibold text-lg mb-4">Kontakt os</h4>
            <ul className="flex flex-col gap-3 text-sm text-stone-600">
              <li className="flex items-start gap-3">
                <i className="fa-solid fa-location-dot mt-1 text-[#67aedd]" />
                <span>Vestermarksvej 72,<br />8305 Samsø</span>
              </li>
              <li className="flex items-center gap-3">
                <i className="fa-solid fa-phone text-[#67aedd]" />
                <a href="tel:+4551540023" className="hover:text-[#67aedd] transition-colors whitespace-nowrap">
                  +45 5154 0023
                </a>
              </li>
              <li className="flex items-center gap-3">
                <i className="fa-solid fa-envelope text-[#67aedd]" />
                <a href="mailto:info@campencamping.dk" className="hover:text-[#67aedd] transition-colors whitespace-nowrap">
                  info@campencamping.dk
                </a>
              </li>
            </ul>
          </div>

          {/* Hours/Info Col */}
          <div>
            <h4 className="text-stone-900 font-serif font-semibold text-lg mb-4">Virksomhed</h4>
            <ul className="flex flex-col gap-2.5 text-sm text-stone-600">
              <li>
                <span className="text-stone-800 font-medium">Firmanavn:</span><br />
                F Campen v/Finn Campen
              </li>
              <li>
                <span className="text-stone-800 font-medium">CVR-nummer:</span><br />
                33438516
              </li>
              <li>
                <Link to="/forretningsbetingelser" className="hover:text-[#67aedd] transition-colors font-medium">
                  Forretningsbetingelser
                </Link>
              </li>
              <li>
                <Link to="/privatlivspolitik" className="hover:text-[#67aedd] transition-colors font-medium">
                  Privatlivspolitik
                </Link>
              </li>
              <li className="text-xs text-stone-500 mt-2">
                Sitet anvender cookies til at forbedre din oplevelse.
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Copyright */}
        <div className="mx-auto max-w-7xl px-6 mt-12 pt-8 border-t border-stone-200 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-stone-500">
          <p>© {new Date().getFullYear()} Campen Camping. Alle rettigheder forbeholdes.</p>
          <p>Udviklet med kærlighed til Samsø</p>
        </div>
      </footer>
    </div>
  );
}
